import { createSlice, createAsyncThunk, PayloadAction, combineReducers } from '@reduxjs/toolkit';
import {
  fetchCourseDetail,
  fetchAllOnlineCollege,
  fetchCourseProgress,
  getReviewStar,
  getByPage,
} from '@cap/share/src/apis/courseDetail';
import { LoadStatus } from '@cap/share/src/components/LoadPlaceholder';
import { ICollege, ICourseDetail, ICourseUserCommentDetailsPage } from './utils/type';
import { getBAccountInfo, getCollegeByTenant } from './utils/func';
import { RootState } from '@/store';
import {
  getTitleBarDataList,
  ICourseTitleBar,
  ICourseProgress,
  initTitleBarProps,
  initProgressProps,
  calcStarTotalData,
  initCourseReview,
  ICourseReview,
} from '@/pages/courseDetail/components/CourseDetail/HeadlineTitle/headlineTitleSlice';
import catalogReducer from '@/pages/courseDetail/components/Catalog/catalogSlice';
import videoModuleReducer from '@/pages/courseDetail/components/VideoModule/videoModuleSlice';

// TODO: 移动到子组件
export const getCourseReviewStar = createAsyncThunk(
  'courseDetailStore/getCourseReviewStar',
  async (props: { id: string; tenant: string }, { getState }) => {
    const state = getState() as RootState;
    const { id, tenant } = props;
    const courseId = parseInt(id, 10);
    const college = state.detailCourseData.base.collegeDetail;
    const tenantInfo = {
      tenantId: college?.id,
      tenant: college?.shortUrl || tenant,
    };
    const res = await getReviewStar({ courseId }, tenantInfo);
    return res.data as ICourseReview; // 返回课程评分
  },
);

// TODO: 移动到子组件
// 用户评论详情
export const getUserCommentDetails = createAsyncThunk(
  'homeStore/getUserCommentDetails',
  async (
    props: { pageNo: number; pageSize: number; id: string; tenant: string; reviewQueryType?: number },
    { getState },
  ) => {
    const state = getState() as RootState;
    const { id, tenant, pageNo, pageSize, reviewQueryType = 1 } = props;
    const courseId = parseInt(id, 10);
    const college = state.detailCourseData.base.collegeDetail;
    const tenantInfo = {
      tenantId: college?.id,
      tenant: college?.shortUrl || tenant,
    };

    const res = (await getByPage(
      { pageNo, pageSize, courseId, reviewQueryType },
      tenantInfo,
    )) as ICourseUserCommentDetailsPage;
    return res;
  },
);

export const getCollegeDetail = createAsyncThunk(
  'courseDetailStore/getAllOnlineCollege',
  async (props: { tenant: string }) => {
    const { tenant } = props;
    const res = await fetchAllOnlineCollege<ICollege[]>();
    const collegeList = res.data;
    const college = getCollegeByTenant(collegeList, tenant);
    return college; // 返回在线学院数据
  },
);

export const getCourseDetail = createAsyncThunk(
  'courseDetailStore/getCourseDetail',
  async (props: { id: string; tenant: string }, { getState }) => {
    const state = getState() as RootState;
    const { id, tenant } = props;
    const courseId = parseInt(id, 10);
    const { collegeDetail } = state.detailCourseData.base;
    const tenantInfo = {
      tenantId: collegeDetail?.id,
      tenant: collegeDetail?.shortUrl || tenant,
    };
    const bAccountInfoDto = await getBAccountInfo();
    const res = await fetchCourseDetail<ICourseDetail>({ courseId, bAccountInfoDto }, tenantInfo);
    return res.data; // 返回课程数据
  },
);

export const getCourseProgress = createAsyncThunk(
  'courseDetailStore/getCourseProgress',
  async (props: { id: any; tenant: string }, { getState }) => {
    const state = getState() as RootState;
    const { id, tenant } = props;
    const courseId = parseInt(id, 10);
    const college = state.detailCourseData.base.collegeDetail;
    const tenantInfo = {
      tenantId: college?.id,
      tenant: college?.shortUrl || tenant,
    };
    try {
      const res = await fetchCourseProgress<ICourseProgress[]>({ courseIds: [courseId] }, tenantInfo);
      return res.data; // 返回学习进度
    } catch (error: any) {
      return [];
    }
  },
);

interface ICourseDetailState {
  pageLoadStatus: LoadStatus;
  collegeDetail: ICollege | null;
  courseDetail: ICourseDetail | null;
  learningProgress: ICourseProgress;

  // TODO: 移动到子组件
  components: {
    courseReviewLoadStatus: LoadStatus;
    titleBarList: ICourseTitleBar;
    content: string;
    courseReview: ICourseReview;
    userCommentDetails: ICourseUserCommentDetailsPage | null;
    contentLoadStatus: LoadStatus;
  };
}

const initialState: ICourseDetailState = {
  pageLoadStatus: LoadStatus.INITIAL,
  collegeDetail: null,
  courseDetail: null,
  learningProgress: initProgressProps,

  // TODO: 移动到子组件
  components: {
    titleBarList: initTitleBarProps,
    content: '',
    courseReview: initCourseReview,
    userCommentDetails: null,
    courseReviewLoadStatus: LoadStatus.INITIAL,
    contentLoadStatus: LoadStatus.INITIAL,
  },
};

export const courseDetailSlice = createSlice({
  name: 'detailCourseModule',
  initialState,
  reducers: {
    changeCollegeDetail: (state, action) => {
      state.collegeDetail = action.payload;
    },
    setContent: (state, action: PayloadAction<string>) => {
      state.components.content = action.payload;
      state.components.contentLoadStatus = LoadStatus.SUCCESS;
    },
    setStudyProgress: (state, action: PayloadAction<ICourseProgress>) => {
      const { maxProgress: oldMaxProgress, nowProgress: oldNowProgress } = state.learningProgress;
      const { maxProgress, nowProgress } = action.payload;
      state.learningProgress.maxProgress = Math.max(maxProgress, oldMaxProgress);
      state.learningProgress.nowProgress = Math.max(nowProgress, oldNowProgress);
    },
  },
  extraReducers: (builder) => {
    builder
      // 学院信息
      .addCase(getCollegeDetail.pending, (state) => {
        state.pageLoadStatus = LoadStatus.LOADING;
      })
      .addCase(getCollegeDetail.fulfilled, (state, action) => {
        if (action.payload) {
          state.collegeDetail = action.payload;
        }
      })
      .addCase(getCollegeDetail.rejected, (state) => {
        state.pageLoadStatus = LoadStatus.FAILURE;
      })
      // 课程详情
      .addCase(getCourseDetail.pending, (state) => {
        state.pageLoadStatus = LoadStatus.LOADING;
      })
      .addCase(getCourseDetail.fulfilled, (state, payload) => {
        state.pageLoadStatus = LoadStatus.SUCCESS;
        const courseDetail = payload.payload;
        state.courseDetail = courseDetail;
        state.components.titleBarList = getTitleBarDataList(courseDetail);
      })
      .addCase(getCourseDetail.rejected, (state) => {
        state.pageLoadStatus = LoadStatus.FAILURE;
      })
      // 学习进度
      .addCase(getCourseProgress.fulfilled, (state, action) => {
        const [learningProgress] = action.payload;
        if (learningProgress) {
          state.learningProgress = learningProgress;
        }
      })

      // TODO: 移动到子组件
      .addCase(getCourseReviewStar.fulfilled, (state, payload) => {
        if (payload.payload) state.components.courseReview = calcStarTotalData(payload.payload) as ICourseReview;
      })
      .addCase(getUserCommentDetails.pending, (state) => {
        state.components.courseReviewLoadStatus = LoadStatus.LOADING;
      })
      .addCase(getUserCommentDetails.fulfilled, (state, payload) => {
        state.components.courseReviewLoadStatus = LoadStatus.SUCCESS;
        state.components.userCommentDetails = payload.payload;
      })
      .addCase(getUserCommentDetails.rejected, (state) => {
        state.components.courseReviewLoadStatus = LoadStatus.FAILURE;
      });
  },
});

export const { changeCollegeDetail, setContent, setStudyProgress } = courseDetailSlice.actions;

const courseDetailReducer = combineReducers({
  base: courseDetailSlice.reducer,
  catalog: catalogReducer,
  videoModule: videoModuleReducer,
});

export default courseDetailReducer;
export const selectCourseLoadStatus = (state: RootState) => state.detailCourseData.base.pageLoadStatus;
export const selectCollegeDetail = (state: RootState) => state.detailCourseData.base.collegeDetail;
export const selectCourseDetail = (state: RootState) => state.detailCourseData.base.courseDetail;
export const selectCourseProgress = (state: RootState) => state.detailCourseData.base.learningProgress;

// TODO: 移动到子组件
export const selectCourseTitleBar = (state: RootState) => state.detailCourseData.base.components.titleBarList;
export const selectCourseContent = (state: RootState) => state.detailCourseData.base.components.content;
export const selectCourseReview = (state: RootState) => state.detailCourseData.base.components.courseReview;
export const selectUserCommentDetails = (state: RootState) => state.detailCourseData.base.components.userCommentDetails;
export const selectUserCommentLoadStatus = (state: RootState) =>
  state.detailCourseData.base.components.courseReviewLoadStatus;
export const selectContentLoadStatus = (state: RootState) => state.detailCourseData.base.components.contentLoadStatus;
